import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Credential } from '../../../../types/credentials';
import FlipIcon from '../../../../components/Icons/FlipIcon';
import CardIcon from './CardIcon';
import ArrowRightIcon from '../../../../components/Icons/ArrowRightIcon';
import CardDetailsModal from '../CardDetailsModal';
import CardFactory from './CardFactory';
import CardBackground from './CardBackground';
import { FontScale, getFluidSpacing } from '../../../../helpers/utils';

interface CardBackProps {
  credential: Credential;
  onFlip?: () => void;
  detailsModal?: boolean;
  detailsModalClose?: () => void;
}

/**
 * @description Card back component
 * @param {CardBackProps} { credential, onFlip }
 * @return {React.FC}
 */
const CardBack: React.FC<CardBackProps> = ({
  credential,
  onFlip,
  detailsModal = false,
  detailsModalClose
}) => {
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const cardBackDetails = CardFactory.createCardBackDetails(credential);

  return (
    <>
      <CardDetailsModal
        credential={credential}
        isVisible={isDetailsModalVisible}
        setIsVisible={setIsDetailsModalVisible}
      />

      <View style={styles.container}>
        <View style={styles.headerContainer}>
          <View style={styles.credentialCardIcon}>
            <CardIcon credential={credential} />
          </View>
          <Text style={[styles.sectionTitle, { fontSize: FontScale.cardHeader() }]}>
            Additional Information
          </Text>
          <Pressable onPress={onFlip} style={styles.flipIconContainer}>
            <FlipIcon iconWidth={20} iconHeight={20} />
          </Pressable>
        </View>
        <View style={styles.cardBackContainer}>
          <Text style={[styles.detailText, { fontSize: FontScale.cardBody() }]}>
            <strong>{credential.name} ~</strong> {credential.issuer}
          </Text>

          {cardBackDetails && (
            <Text style={[styles.detailText, { fontSize: FontScale.cardBody() }]}>
              {cardBackDetails.issuer_desc}
            </Text>
          )}
          {cardBackDetails && (
            <Text style={[styles.detailText, { fontSize: FontScale.link() }]}>
              <a
                href={cardBackDetails.link}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
                title={cardBackDetails.link_desc}
                aria-label={cardBackDetails.link_desc}>
                {cardBackDetails.link}
              </a>
            </Text>
          )}

          {!detailsModal && (
            <Pressable onPress={() => setIsDetailsModalVisible(true)} style={styles.detailsButton}>
              <ArrowRightIcon iconWidth={20} iconHeight={20} color="#fff" />
            </Pressable>
          )}
          {detailsModal && (
            <Pressable onPress={detailsModalClose} style={styles.detailsButton}>
              <View style={{ transform: [{ rotate: '180deg' }] }}>
                <ArrowRightIcon iconWidth={20} iconHeight={20} color="#fff" />
              </View>
            </Pressable>
          )}
        </View>
      </View>
      <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
        <CardBackground credential={credential} />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    //paddingHorizontal: 16,
    gap: getFluidSpacing(16, 18, 20),
    zIndex: 1
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    zIndex: 1
  },
  flipIconContainer: {
    zIndex: 2
  },
  credentialCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 4,
    overflow: 'hidden'
  },
  cardDetailButtonContainer: {
    position: 'relative',
    width: '100%',
    flex: 1
  },
  cardBackContainer: {
    flex: 3,
    width: '100%',
    zIndex: 1,
    position: 'relative'
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: '600'
  },
  detailText: {
    color: '#fff',
    fontWeight: '400',
    marginBottom: 8
  },
  detailsButton: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    display: 'flex',
    color: '#fff',
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  link: {
    color: '#fff',
    textDecorationLine: 'underline'
  }
});

export default CardBack;
